// src/pages/Auth/ForgotPassword.tsx - COMPLETE FIXED VERSION
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../api';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/input';
import toast from 'react-hot-toast';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    const toastId = toast.loading('Sending reset link...');
    
    try {
      const response = await authAPI.forgotPassword({ email });
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Reset link sent! Check your email.', { id: toastId });
      } else {
        setError(response.data.message || 'Failed to send reset email');
        toast.error(response.data.message || 'Failed to send reset email', { id: toastId });
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      const errorMsg = error.response?.data?.message || 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full">
        <Link to="/login" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Login
        </Link>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password</h1>
        <p className="text-gray-600 mb-6">
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="pl-10 py-3"
                required
                disabled={loading}
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full py-3 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
        ) : (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Check Your Email</h2>
            <p className="text-gray-600 mb-4">
              We've sent a password reset link to <strong className="text-emerald-600">{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="text-emerald-600 hover:text-emerald-700 font-medium"
            >
              ← Try again with different email
            </button>
          </div>
        )}
        
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>© 2024 CarryNGo. Operating in Osogbo, Nigeria 🇳🇬</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;